import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, ExternalLink, Eye, EyeOff, Copy, Play, ChevronDown, ChevronRight, Search } from "lucide-react";
import { inferContent } from "../lib/contentKind";

// Shared helper — single source of truth for URL → embed URL conversion.
// Falls back to the raw URL for anything not recognized (matches old behavior).
function getEmbedUrl(url) {
  if (!url) return null;
  const { embedUrl } = inferContent(url);
  return embedUrl || url;
}

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tools, setTools] = useState([]);
  const [showTools, setShowTools] = useState(false);
  const [showAllPillars, setShowAllPillars] = useState(false);
  const [showLessons, setShowLessons] = useState(true);
  const [search, setSearch] = useState("");

  const isAdmin = user?.role?.toLowerCase() === "admin";

  useEffect(() => { load(); loadTools(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/onboarding/my-state");
      setState(r.data);
    } catch (e) { console.error(e); toast.error("Failed to load onboarding"); }
    setLoading(false);
  };

  const loadTools = async () => {
    try { const r = await api.get("/onboarding/tools"); setTools(r.data || []); } catch {}
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] font-heading">LOADING...</div></div>;
  if (!state) return null;

  const { current_pillar, timeline, completed_count, total_count } = state;
  const progressPct = total_count ? Math.round((completed_count / total_count) * 100) : 0;

  return (
    <div className="dm-page-container max-w-[1100px] mx-auto" style={{ padding: 24 }}>
      {/* Top bar with progress */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl text-[var(--t1)] tracking-wide">ONBOARDING</h1>
          <p className="text-xs text-[var(--t3)] font-mono mt-1">{completed_count}/{total_count} pillars complete · {progressPct}%</p>
        </div>
        {isAdmin && (
          <button onClick={() => navigate("/admin/onboarding")}
            className="flex items-center gap-1.5 px-4 py-2 bg-[var(--r)] text-white text-xs font-bold rounded-lg hover:shadow-lg transition-all">
            ⚙ Manage Content
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-[var(--s2)] rounded-full overflow-hidden mb-6">
        <div className="h-full bg-[var(--g)]" style={{ width: `${progressPct}%`, transition: "width .4s" }} />
      </div>

      {/* RIGHT NOW — current pillar */}
      {current_pillar ? (
        <CurrentPillarSection pillar={current_pillar} onUpdate={load} isAdmin={isAdmin} />
      ) : (
        <div className="text-center py-16 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl">
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <p className="text-[var(--t1)] font-heading text-xl mb-2">All pillars complete!</p>
          <p className="text-[var(--t3)] text-sm">You've completed onboarding. Welcome aboard.</p>
        </div>
      )}

      {/* TOOLS — collapsible */}
      <CollapsibleSection
        title={`Tools · ${tools.length}`}
        open={showTools}
        onToggle={() => setShowTools(!showTools)}
      >
        <ToolsGrid tools={tools} isAdmin={isAdmin} onChange={loadTools} />
      </CollapsibleSection>

      {/* ALL PILLARS — collapsible */}
      <CollapsibleSection
        title={`All ${total_count} pillars`}
        open={showAllPillars}
        onToggle={() => setShowAllPillars(!showAllPillars)}
      >
        <PillarsTimeline timeline={timeline} currentId={current_pillar?.pillar_id} />
      </CollapsibleSection>
    </div>
  );
}

// ════════════════════════════════════════
// CURRENT PILLAR (the "RIGHT NOW" section)
// ════════════════════════════════════════
function CurrentPillarSection({ pillar, onUpdate, isAdmin }) {
  const navigate = useNavigate();
  const lessons = pillar.lessons || [];
  const completed = pillar.progress?.lessons_completed || [];
  const allLessonsDone = lessons.length > 0 && lessons.every(l => completed.includes(l.lesson_id));
  const briefs = pillar.assigned_briefs || [];
  const lessonsPct = lessons.length ? Math.round((completed.length / lessons.length) * 100) : 0;

  // Find next unfinished lesson
  const nextLesson = lessons.find(l => !completed.includes(l.lesson_id));

  return (
    <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl overflow-hidden mb-4">
      {/* Pillar header */}
      <div className="px-6 py-5 border-b border-[var(--bdr)]">
        <div className="flex items-center gap-3 mb-1">
          <span style={{ fontSize: 24 }}>{pillar.icon}</span>
          <h2 className="font-heading text-2xl text-[var(--t1)]">{pillar.title}</h2>
        </div>
        <p className="text-[var(--t3)] text-sm">{pillar.description}</p>
        {lessons.length > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-1 bg-[var(--s2)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--c)]" style={{ width: `${lessonsPct}%`, transition: "width .3s" }} />
            </div>
            <span className="text-[10px] text-[var(--t3)] font-mono">{completed.length}/{lessons.length} lessons</span>
          </div>
        )}
      </div>

      {/* Lessons */}
      {lessons.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📚</div>
          <p className="text-[var(--t2)] text-sm font-medium mb-1">No lessons in this pillar yet</p>
          <p className="text-[var(--t3)] text-xs mb-4">
            {isAdmin ? "Add Looms and deck links below to bring this pillar to life." : "Your admin is setting up content. Check back soon."}
          </p>
          {isAdmin && (
            <button onClick={() => navigate("/admin/onboarding")}
              className="px-4 py-2 bg-[var(--r)] text-white text-xs font-bold rounded-lg">
              + Add Lessons
            </button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-[var(--bdr)]">
          {lessons.map((lesson, i) => (
            <LessonCard
              key={lesson.lesson_id}
              lesson={lesson}
              completed={completed.includes(lesson.lesson_id)}
              isCurrent={lesson.lesson_id === nextLesson?.lesson_id}
              onUpdate={onUpdate}
            />
          ))}
        </div>
      )}

      {/* Assignment section */}
      <div className="px-6 py-4 bg-[var(--s2)] border-t border-[var(--bdr)]">
        <h3 className="text-xs font-heading text-[var(--t2)] tracking-wider mb-2">YOUR ASSIGNMENT</h3>
        {briefs.length === 0 ? (
          allLessonsDone ? (
            <p className="text-[var(--t3)] text-sm">⏳ Waiting for your manager to assign a live brief as your assessment.</p>
          ) : (
            <p className="text-[var(--t3)] text-sm">Complete all lessons first, then your manager will assign a brief.</p>
          )
        ) : (
          <div className="space-y-2">
            {briefs.map(b => <BriefAssignment key={b.brief_id} brief={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// LESSON CARD
// ════════════════════════════════════════
function LessonCard({ lesson, completed, isCurrent, onUpdate }) {
  const [expanded, setExpanded] = useState(isCurrent && !completed);
  const [marking, setMarking] = useState(false);
  const embed = getEmbedUrl(lesson.loom_url);

  const toggleComplete = async () => {
    setMarking(true);
    try {
      const url = completed ? `/onboarding/lessons/${lesson.lesson_id}/uncomplete` : `/onboarding/lessons/${lesson.lesson_id}/complete`;
      await api.post(url);
      await onUpdate();
    } catch { toast.error("Failed to update"); }
    setMarking(false);
  };

  const typeIcon = lesson.type === "video" ? "🎥" : lesson.type === "physical" ? "🧑‍🏫" : "📖";

  return (
    <div className={`px-6 py-3 ${isCurrent && !completed ? "bg-[var(--rd)]" : ""}`}>
      <div className="flex items-center gap-3">
        <button onClick={toggleComplete} disabled={marking}
          className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            completed ? "bg-[var(--g)] border-[var(--g)]" : "border-[var(--bdr)] hover:border-[var(--g)]"
          }`}>
          {completed && <Check size={12} color="white" />}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 14 }}>{typeIcon}</span>
            <p className={`text-sm font-medium ${completed ? "line-through text-[var(--t3)]" : "text-[var(--t1)]"}`}>{lesson.title}</p>
            {lesson.duration_minutes > 0 && <span className="text-[10px] text-[var(--t3)] font-mono">{lesson.duration_minutes}m</span>}
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 ml-8 space-y-3">
          {lesson.description && <p className="text-[var(--t2)] text-sm">{lesson.description}</p>}

          {/* Video player */}
          {embed && (
            <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: "56.25%" }}>
              <iframe src={embed} className="absolute inset-0 w-full h-full" allow="fullscreen" frameBorder="0" />
            </div>
          )}

          {/* Physical class details */}
          {lesson.type === "physical" && lesson.physical_details && (
            <div className="bg-[var(--s2)] border border-[var(--bdr)] rounded-lg p-3 text-sm text-[var(--t2)]">
              <strong className="text-[var(--t1)]">📍 In-person session:</strong><br />{lesson.physical_details}
            </div>
          )}

          {/* Reading link */}
          {lesson.type === "reading" && lesson.reading_url && (
            <a href={lesson.reading_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] hover:bg-[var(--s3)]">
              📖 Open reading material <ExternalLink size={12} />
            </a>
          )}

          {/* Deck links */}
          <div className="flex flex-wrap gap-2">
            {lesson.deck_view_url && (
              <a href={lesson.deck_view_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-xs text-[var(--t1)] hover:bg-[var(--s3)]">
                📄 View deck <ExternalLink size={11} />
              </a>
            )}
            {lesson.deck_edit_url && (
              <a href={lesson.deck_edit_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--rd)] border border-[var(--r)] rounded-lg text-xs text-[var(--r)] hover:bg-[var(--rg)]">
                ✏️ Try editing a copy <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════
// BRIEF ASSIGNMENT
// ════════════════════════════════════════
function BriefAssignment({ brief }) {
  const navigate = useNavigate();
  const scored = brief.training_score !== null && brief.training_score !== undefined;
  const passed = brief.training_passed;

  return (
    <div onClick={() => navigate(`/bizdev/briefs/${brief.brief_id}`)}
      className="flex items-center gap-3 p-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg cursor-pointer hover:border-[var(--bdrh)]">
      <span style={{ fontSize: 18 }}>📄</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--t1)]">{brief.title}</p>
        <p className="text-[10px] text-[var(--t3)]">{brief.company_name} · {brief.status}</p>
      </div>
      {scored ? (
        <div className={`text-right ${passed ? "text-[var(--g)]" : "text-[var(--r)]"}`}>
          <p className="text-sm font-bold">{brief.training_score}/100</p>
          <p className="text-[10px]">{passed ? "✅ Passed" : "❌ Retry"}</p>
        </div>
      ) : (
        <span className="text-xs text-[var(--t3)]">Open →</span>
      )}
    </div>
  );
}

// ════════════════════════════════════════
// COLLAPSIBLE SECTION
// ════════════════════════════════════════
function CollapsibleSection({ title, open, onToggle, children }) {
  return (
    <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl mb-4 overflow-hidden">
      <button onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--s2)] transition-colors">
        <span className="font-heading text-sm text-[var(--t1)] tracking-wide">{title}</span>
        {open ? <ChevronDown size={18} color="var(--t3)" /> : <ChevronRight size={18} color="var(--t3)" />}
      </button>
      {open && <div className="border-t border-[var(--bdr)]">{children}</div>}
    </div>
  );
}

// ════════════════════════════════════════
// TOOLS GRID
// ════════════════════════════════════════
function ToolsGrid({ tools, isAdmin, onChange }) {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [openTool, setOpenTool] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editTool, setEditTool] = useState(null);

  const categories = ["All", ...new Set(tools.map(t => t.category))];
  const filtered = tools.filter(t => {
    if (filter !== "All" && t.category !== filter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4">
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tool..."
          className="px-3 py-1.5 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none focus:border-[var(--bdrh)] w-48" />
        <div className="flex gap-1 flex-wrap">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                filter === c ? "bg-[var(--t1)] text-white" : "bg-[var(--s2)] text-[var(--t3)] hover:text-[var(--t1)]"
              }`}>
              {c}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-[var(--r)] text-white text-xs font-bold rounded-lg">
            <Plus size={12} /> Add Tool
          </button>
        )}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <p className="text-center text-[var(--t3)] text-sm py-8">No tools yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filtered.map(t => (
            <div key={t.tool_id} className="group relative">
              <button onClick={() => setOpenTool(t)}
                className="w-full p-3 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg hover:border-[var(--bdrh)] hover:bg-[var(--s3)] transition-all text-center">
                {t.login_url ? (
                  <img src={`https://www.google.com/s2/favicons?domain=${t.login_url.replace(/https?:\/\//, '').split('/')[0]}&sz=64`}
                    className="w-8 h-8 mx-auto mb-2" alt="" onError={e => e.target.style.display = "none"} />
                ) : <div className="w-8 h-8 mx-auto mb-2 bg-[var(--s3)] rounded" />}
                <p className="text-xs font-medium text-[var(--t1)] truncate">{t.name}</p>
                <p className="text-[9px] text-[var(--t3)]">{t.category}</p>
              </button>
              {isAdmin && (
                <button onClick={() => setEditTool(t)}
                  className="absolute top-1 right-1 p-1 bg-[var(--s1)] rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pencil size={10} color="var(--t3)" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {openTool && <ToolCredentialsModal tool={openTool} onClose={() => setOpenTool(null)} />}
      {showAdd && <ToolEditModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onChange(); }} />}
      {editTool && <ToolEditModal tool={editTool} onClose={() => setEditTool(null)} onSaved={() => { setEditTool(null); onChange(); }} />}
    </div>
  );
}

// ════════════════════════════════════════
// TOOL CREDENTIALS MODAL
// ════════════════════════════════════════
function ToolCredentialsModal({ tool, onClose }) {
  const [creds, setCreds] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    api.get(`/onboarding/tools/${tool.tool_id}/credentials`).then(r => setCreds(r.data)).catch(() => {});
  }, [tool.tool_id]);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 20, width: "min(420px, 90vw)" }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading text-lg text-[var(--t1)]">{tool.name}</h3>
            <p className="text-xs text-[var(--t3)]">{tool.category}</p>
          </div>
          <button onClick={onClose} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]"><X size={16} /></button>
        </div>

        {tool.login_url && (
          <a href={tool.login_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-[var(--c)]/10 border border-[var(--c)]/30 rounded-lg text-sm text-[var(--c)] mb-3 hover:bg-[var(--c)]/20">
            🔗 Open login page <ExternalLink size={12} />
          </a>
        )}

        {creds ? (
          <div className="space-y-2">
            {creds.username && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg">
                <span className="text-xs text-[var(--t3)] w-16">Username</span>
                <span className="text-sm text-[var(--t1)] flex-1 font-mono">{creds.username}</span>
                <button onClick={() => copy(creds.username)} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]"><Copy size={12} /></button>
              </div>
            )}
            {creds.password && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg">
                <span className="text-xs text-[var(--t3)] w-16">Password</span>
                <span className="text-sm text-[var(--t1)] flex-1 font-mono">{showPwd ? creds.password : "•".repeat(Math.min(creds.password.length, 12))}</span>
                <button onClick={() => setShowPwd(!showPwd)} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]">{showPwd ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                <button onClick={() => copy(creds.password)} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]"><Copy size={12} /></button>
              </div>
            )}
            {creds.notes && (
              <div className="px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg">
                <p className="text-xs text-[var(--t3)] mb-1">Notes</p>
                <p className="text-sm text-[var(--t2)]">{creds.notes}</p>
              </div>
            )}
          </div>
        ) : <p className="text-sm text-[var(--t3)]">Loading...</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// TOOL EDIT MODAL (admin)
// ════════════════════════════════════════
function ToolEditModal({ tool, onClose, onSaved }) {
  const [form, setForm] = useState(tool || { name: "", category: "Other", login_url: "", username: "", password: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const isEdit = !!tool;

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/onboarding/tools/${tool.tool_id}`, form);
      else await api.post("/onboarding/tools", form);
      toast.success(isEdit ? "Updated" : "Tool added");
      onSaved();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this tool?")) return;
    try { await api.delete(`/onboarding/tools/${tool.tool_id}`); toast.success("Deleted"); onSaved(); }
    catch { toast.error("Failed to delete"); }
  };

  const inputCls = "w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none focus:border-[var(--bdrh)]";
  const labelCls = "block text-xs font-semibold text-[var(--t2)] mb-1";

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 20, width: "min(440px, 92vw)", maxHeight: "85vh", overflow: "auto" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-[var(--t1)]">{isEdit ? "Edit Tool" : "Add Tool"}</h3>
          <button onClick={onClose} className="p-1 text-[var(--t3)]"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div><label className={labelCls}>Name *</label><input className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Canva Pro" /></div>
          <div><label className={labelCls}>Category</label>
            <select className={inputCls} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              {["AI","Design","Analytics","Video","Social","Productivity","Client Tools","Other"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Login URL</label><input className={inputCls} value={form.login_url} onChange={e => setForm({ ...form, login_url: e.target.value })} placeholder="https://..." /></div>
          <div><label className={labelCls}>Username / Email</label><input className={inputCls} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} /></div>
          <div><label className={labelCls}>Password</label><input className={inputCls} type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
          <div><label className={labelCls}>Notes</label><textarea className={inputCls} rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="flex-1 py-2 bg-[var(--r)] text-white font-bold rounded-lg text-sm">{saving ? "Saving..." : "Save"}</button>
            {isEdit && <button onClick={del} className="px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--r)]"><Trash2 size={14} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// PILLARS TIMELINE
// ════════════════════════════════════════
function PillarsTimeline({ timeline, currentId }) {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div className="p-4 space-y-2">
      {timeline.map((p, i) => {
        const isCurrent = p.pillar_id === currentId;
        const isPassed = p.status === "passed";
        const isFailed = p.status === "failed";
        const score = p.latest_score;
        const feedback = p.latest_feedback;
        const hasDetails = feedback && (isPassed || isFailed);
        const expanded = expandedId === p.pillar_id;
        return (
          <div key={p.pillar_id}
            className={`rounded-lg ${isCurrent ? "bg-[var(--rd)] border border-[var(--r)]" : "bg-[var(--s2)] border border-transparent"}`}>
            <div className={`flex items-center gap-3 px-3 py-2 ${hasDetails ? "cursor-pointer" : ""}`}
              onClick={() => hasDetails && setExpandedId(expanded ? null : p.pillar_id)}>
              <span className="text-xs font-mono text-[var(--t3)] w-6">{i + 1}.</span>
              <span style={{ fontSize: 16 }}>{p.icon}</span>
              <span className={`flex-1 text-sm ${isPassed ? "text-[var(--t3)]" : "text-[var(--t1)]"}`}>{p.title}</span>
              {isPassed && <span className="text-xs text-[var(--g)] font-semibold">✓ {score || ""}/100</span>}
              {isFailed && <span className="text-xs text-[var(--r)] font-semibold">❌ {score || 0}/100 · Retry</span>}
              {isCurrent && !isPassed && !isFailed && <span className="text-xs text-[var(--r)]">▶ Now</span>}
              {hasDetails && <span className="text-xs text-[var(--t4)]">{expanded ? "▲" : "▼"}</span>}
            </div>
            {expanded && hasDetails && (
              <div className="px-3 pb-3 ml-9">
                <p className="text-xs text-[var(--t2)] italic">"{feedback}"</p>
                {p.latest_brief_title && <p className="text-[10px] text-[var(--t3)] mt-1">— on: {p.latest_brief_title}</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
